import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';

const LOGO_URL = "/icon-v2-512.png";

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success('Welcome back!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container px-4" style={{ background: 'linear-gradient(180deg, #0B1F3A 0%, #1a3a8f 50%, #F2F5F7 50%)' }}>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <img 
            src={LOGO_URL} 
            alt="AegisPay Logo" 
            className="w-24 h-24 mx-auto mb-4 rounded-2xl object-cover shadow-lg"
          />
          <h1 className="text-3xl font-bold text-white">Aegis<span className="text-[#1E5EFF]">Pay</span></h1>
          <p className="text-blue-200 mt-1">Save smarter with group savings</p>
          <p className="text-blue-200/70 italic text-[11px] mt-1">Your money follows rules, not promises</p>
          <p className="text-blue-300/60 text-xs mt-0.5">app.aegispay.app</p>
        </div>

        <Card className="glass-card border-0 shadow-xl">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl text-[#0B1F3A]">Welcome back</CardTitle>
            <CardDescription>Sign in to continue to AegisPay</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="h-12 rounded-xl bg-slate-50"
                  data-testid="login-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-12 rounded-xl bg-slate-50 pr-10"
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-[#0B1F3A] hover:bg-[#1a3a8f] text-white font-medium btn-press"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            <div className="mt-4 text-center">
              <Link to="/forgot-password" className="text-sm text-[#2272C5] font-medium hover:underline" data-testid="forgot-password-link">
                Forgot your password?
              </Link>
            </div>

            <div className="mt-4 text-center">
              <p className="text-sm text-slate-500">
                Don't have an account?{' '}
                <Link to="/register" className="text-[#2272C5] font-medium hover:underline" data-testid="register-link">
                  Create one
                </Link>
              </p>
            </div>

            {/* Demo Notice */}
            <div className="mt-6 p-3 rounded-xl bg-amber-50 border border-amber-200">
              <p className="text-xs text-amber-700 text-center">
                <span className="demo-badge mr-2">DEMO</span>
                This is a demo app. All funds are simulated.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
